import { uid, routePath, routeTiming, routeLength, routeEnd, sortedEvents, playerRoutes } from "@/lib/playEngine";
import { BALL_STYLES } from "@/lib/playStyles";

export const CONTENT_KEYS = ["name", "category", "formation", "tags", "description", "favorite", "los_y", "first_down_y", "players", "routes", "ball", "phases", "quiz"];
export const contentOf = (play) => Object.fromEntries(CONTENT_KEYS.map((k) => [k, play[k]]));

const autoDur = (play, r) => Math.max(0.5, Math.round((routeLength(play, r) / 6) * 10) / 10);
const r1 = (v) => Math.round(v * 10) / 10;

export const updatePlayer = (play, id, patch) => ({ ...play, players: play.players.map((p) => (p.id === id ? { ...p, ...patch } : p)) });

export function addPlayer(play, side, label, x, y) {
  const p = { id: uid(side[0]), side, label, nickname: "", number: "", x, y, lineup: "", before_snap: "", when_move: "", where_move: "", instruction: "", phase_notes: {} };
  return [{ ...play, players: [...play.players, p] }, p.id];
}

export function removePlayer(play, id, reassignTo = null) {
  const events = reassignTo
    ? play.ball.events.map((e) => ({ ...e, from_id: e.from_id === id ? reassignTo : e.from_id, to_id: e.to_id === id ? reassignTo : e.to_id })).filter((e) => e.from_id !== e.to_id)
    : play.ball.events.filter((e) => e.from_id !== id && e.to_id !== id);
  const carrier = play.ball.carrier === id ? reassignTo : play.ball.carrier;
  return {
    ...play,
    players: play.players.filter((p) => p.id !== id),
    routes: play.routes.filter((r) => r.player_id !== id),
    ball: { carrier, events },
    quiz: play.quiz.map((q) => (q.position_id === id ? { ...q, position_id: null } : q)),
  };
}

export function updateRoute(play, rid, patch) {
  const routes = play.routes.map((r) => (r.id === rid ? { ...r, ...patch } : r));
  const next = { ...play, routes };
  return { ...next, routes: routes.map((r) => (r.id === rid && r.auto_time ? { ...r, duration: autoDur(next, r) } : r)) };
}

export function newRoute(play, pid, type, pt, snap) {
  const prev = playerRoutes(play, pid);
  const last = prev[prev.length - 1];
  const start = last ? r1(routeEnd(play, last)) : type === "motion" ? 0 : snap;
  const r = { id: uid("r"), player_id: pid, type, curve: "sharp", points: [pt], start, duration: 1, auto_time: true };
  const next = { ...play, routes: [...play.routes, r] };
  return [updateRoute(next, r.id, {}), r.id];
}

export const addPoint = (play, rid, pt) => updateRoute(play, rid, { points: [...play.routes.find((r) => r.id === rid).points, pt] });
export const movePoint = (play, rid, idx, pt) =>
  updateRoute(play, rid, { points: play.routes.find((r) => r.id === rid).points.map((p, i) => (i === idx ? { ...p, ...pt } : p)) });

export const SPEEDS = { walk: { label: "Walk", yps: 2 }, jog: { label: "Jog", yps: 4 }, sprint: { label: "Sprint", yps: 7 } };

export function setRouteSpeed(play, rid, yps) {
  const r = play.routes.find((x) => x.id === rid);
  const cleared = { ...r, points: r.points.map((p) => ({ ...p, dur: null })) };
  const next = { ...play, routes: play.routes.map((x) => (x.id === rid ? cleared : x)) };
  const len = routeLength(next, cleared);
  const oldEnd = routeEnd(play, r);
  const sped = { ...next, routes: next.routes.map((x) => (x.id === rid ? { ...x, duration: Math.max(0.1, Math.round((len / yps) * 10) / 10), auto_time: false } : x)) };
  const delta = routeEnd(sped, sped.routes.find((x) => x.id === rid)) - oldEnd;
  return { ...sped, routes: sped.routes.map((x) => (x.player_id === r.player_id && x.id !== rid && x.start >= oldEnd - 0.05 ? { ...x, start: Math.max(0, r1(x.start + delta)) } : x)) };
}

export function copyRoutes(play, fromId, toId, mirror) {
  const src = play.players.find((p) => p.id === fromId), dst = play.players.find((p) => p.id === toId);
  const flipY = src.side !== dst.side ? -1 : 1;
  const map = (q) => ({ ...q, x: r1(Math.min(52.8, Math.max(0.5, dst.x + (q.x - src.x) * (mirror ? -1 : 1)))), y: r1(dst.y + (q.y - src.y) * flipY) });
  const copies = playerRoutes(play, fromId).map((r) => ({ ...r, id: uid("r"), player_id: toId, points: r.points.map(map) }));
  return { ...play, routes: [...play.routes.filter((r) => r.player_id !== toId), ...copies] };
}

export function movePlayerWithRoutes(play, id, pt) {
  const pl = play.players.find((p) => p.id === id);
  const dx = pt.x - pl.x, dy = pt.y - pl.y;
  const sh = (q) => ({ ...q, x: r1(q.x + dx), y: r1(q.y + dy) });
  return {
    ...play,
    players: play.players.map((p) => (p.id === id ? { ...p, x: pt.x, y: pt.y } : p)),
    routes: play.routes.map((r) => (r.player_id === id ? { ...r, points: r.points.map(sh) } : r)),
  };
}

export function insertPoint(play, rid, idx) {
  const r = play.routes.find((x) => x.id === rid);
  const path = routePath(play, r);
  const a = path[idx], b = path[idx + 1];
  const pts = [...r.points];
  pts.splice(idx, 0, { x: r1((a.x + b.x) / 2), y: r1((a.y + b.y) / 2) });
  return updateRoute(play, rid, { points: pts });
}

export function deletePoint(play, rid, idx) {
  const r = play.routes.find((x) => x.id === rid);
  if (r.points.length <= 1) return deleteRoute(play, rid);
  return updateRoute(play, rid, { points: r.points.filter((_, i) => i !== idx) });
}

export const patchPoint = (play, rid, idx, patch) =>
  ({ ...play, routes: play.routes.map((r) => (r.id === rid ? { ...r, points: r.points.map((p, i) => (i === idx ? { ...p, ...patch } : p)) } : r)) });

export function setSegmentDur(play, rid, idx, v) {
  const r = play.routes.find((x) => x.id === rid);
  const times = routeTiming(play, r).legs.map((l) => l.t1 - l.t0);
  if (v == null) return { ...play, routes: play.routes.map((x) => (x.id === rid ? { ...x, points: x.points.map((p, i) => (i === idx ? { ...p, dur: null } : p)) } : x)) };
  times[idx] = v;
  const points = r.points.map((p, i) => (i === idx ? { ...p, dur: v } : p));
  return { ...play, routes: play.routes.map((x) => (x.id === rid ? { ...x, points, duration: Math.round(times.reduce((s, t) => s + t, 0) * 100) / 100, auto_time: false } : x)) };
}

export function applyPreset(play, pid, points, type, start) {
  const r = { id: uid("r"), player_id: pid, type, curve: "sharp", points, start, duration: 1, auto_time: true };
  const next = { ...play, routes: [...play.routes.filter((x) => x.player_id !== pid), r] };
  return [updateRoute(next, r.id, {}), r.id];
}

export function transformRoute(play, rid, kind) {
  const r = play.routes.find((x) => x.id === rid);
  const o = routePath(play, r)[0];
  const pl = play.players.find((p) => p.id === r.player_id);
  const ang = ((kind === "rotL" ? -15 : 15) * Math.PI) / 180;
  const tf = (p) => {
    let dx = p.x - o.x, dy = p.y - o.y;
    if (kind === "mirror") dx = -dx;
    else if (kind === "grow" || kind === "shrink") { const s = kind === "grow" ? 1.2 : 0.8; dx *= s; dy *= s; }
    else { const c = Math.cos(ang), s = Math.sin(ang); [dx, dy] = [dx * c - dy * s, dx * s + dy * c]; }
    return { ...p, x: r1(Math.min(52.8, Math.max(0.5, o.x + dx))), y: r1(o.y + dy) };
  };
  return pl ? updateRoute(play, rid, { points: r.points.map(tf) }) : play;
}
export const removeLastPoint = (play, rid) => {
  const r = play.routes.find((x) => x.id === rid);
  if (r.points.length <= 1) return deleteRoute(play, rid);
  return updateRoute(play, rid, { points: r.points.slice(0, -1) });
};
export const deleteRoute = (play, rid) => ({ ...play, routes: play.routes.filter((r) => r.id !== rid) });

export function addBallEvent(play, type, fromId, toId) {
  const evs = sortedEvents(play);
  const lastEnd = evs.length ? Math.max(...evs.map((e) => e.time + e.duration)) : 0.5;
  const time = type === "snap" ? 1 : Math.round((lastEnd + 0.5) * 10) / 10;
  const e = { id: uid("b"), type, from_id: fromId, to_id: toId, time, duration: BALL_STYLES[type].duration };
  const carrier = play.ball.carrier || (type === "snap" ? fromId : play.ball.carrier);
  return { ...play, ball: { carrier, events: [...play.ball.events, e] } };
}

export const updateEvent = (play, id, patch) => ({ ...play, ball: { ...play.ball, events: play.ball.events.map((e) => (e.id === id ? { ...e, ...patch } : e)) } });
export const deleteEvent = (play, id) => ({ ...play, ball: { ...play.ball, events: play.ball.events.filter((e) => e.id !== id) } });

export function shiftLos(play, dy) {
  const mv = (p) => ({ ...p, y: Math.round((p.y + dy) * 10) / 10 });
  return {
    ...play,
    los_y: play.los_y + dy,
    first_down_y: play.first_down_y + dy,
    players: play.players.map(mv),
    routes: play.routes.map((r) => ({ ...r, points: r.points.map(mv) })),
    quiz: play.quiz.map((q) => (q.target ? { ...q, target: mv(q.target) } : q)),
  };
}

export const updateQuestion = (play, id, patch) => ({ ...play, quiz: play.quiz.map((q) => (q.id === id ? { ...q, ...patch } : q)) });
